import React, { useState, useRef, useEffect } from "react";
import { aiApi } from "@/lib/api";
import { MessageSquare, X, Send, Minimize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Link, useLocation } from "react-router-dom";

export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const loc = useLocation();

  if (loc.pathname.startsWith("/assistant") || loc.pathname === "/login") return null;

  const send = async () => {
    const msg = input.trim();
    if (!msg) return;
    setInput(""); setLoading(true);
    setMessages(m => [...m, { role: "user", content: msg }]);
    try {
      const r = await aiApi.chat(msg, sessionId);
      setSessionId(r.data.session_id);
      setMessages(m => [...m, { role: "assistant", content: r.data.reply }]);
    } catch(e) {
      toast.error(e.response?.data?.detail || "Assistant is unavailable");
      setMessages(m => m.slice(0, -1));
    } finally { setLoading(false); setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50); }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-[calc(72px+env(safe-area-inset-bottom))] right-4 md:bottom-6 md:right-6 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white text-black shadow-2xl hover:bg-white/90 flex items-center justify-center transition-transform hover:scale-105 z-40"
          data-testid="assistant-widget-fab"
          title="Executive Assistant"
          aria-label="Open Executive Assistant"
        >
          <MessageSquare className="w-5 h-5"/>
        </button>
      )}

      {open && (
        <div
          className="fixed inset-x-2 bottom-[calc(72px+env(safe-area-inset-bottom))] top-20 md:inset-x-auto md:top-auto md:bottom-6 md:right-6 md:w-96 md:h-[600px] bg-[#151A23] border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden z-50"
          data-testid="assistant-widget"
        >
          <div className="h-14 border-b border-white/10 flex items-center px-4 bg-[#1E2532]">
            <MessageSquare className="w-4 h-4 text-white/70 mr-2"/>
            <div className="text-sm font-semibold flex-1">Executive Assistant</div>
            <Link to="/assistant" className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white" title="Open full view" onClick={() => setOpen(false)}>
              <Minimize2 className="w-3.5 h-3.5"/>
            </Link>
            <button onClick={() => setOpen(false)} className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white" data-testid="assistant-widget-close" aria-label="Close">
              <X className="w-4 h-4"/>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-3"><MessageSquare className="w-5 h-5 text-white/60"/></div>
                <p className="text-sm text-white/60">Ask about anything in HQ.</p>
                <div className="mt-4 space-y-1.5 text-left">
                  {["What needs my attention today?","What's overdue?","Summarize progress."].map(s => (
                    <button key={s} onClick={() => { setInput(s); setTimeout(send, 50); }} className="w-full text-left px-3 py-2 rounded-md bg-white/[0.03] hover:bg-white/[0.08] text-xs text-white/80 border border-white/5">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`p-3 rounded-lg max-w-[85%] text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-white text-black" : "bg-white/[0.05] border border-white/10 text-white/90"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && <div className="text-xs text-white/50 pl-1">Working on it…</div>}
            <div ref={endRef}/>
          </div>

          <div className="border-t border-white/10 p-3">
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
              <Input value={input} onChange={e=>setInput(e.target.value)} placeholder="Ask…" className="flex-1 bg-white/5 border-white/10 h-9 text-sm" disabled={loading} data-testid="assistant-widget-input"/>
              <Button type="submit" size="sm" disabled={loading || !input.trim()} className="rounded-full bg-white text-black hover:bg-white/90 h-9 w-9 p-0" data-testid="assistant-widget-send"><Send className="w-3.5 h-3.5"/></Button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
